/**
 * Theme Context for managing dynamic button colors
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Predefined themes with hex colors
const predefinedThemes = {
  classic: {
    name: 'classic',
    colors: {
      buy: '#10b981',
      sell: '#ef4444'
    }
  },
  ocean: {
    name: 'ocean',
    colors: {
      buy: '#0ea5e9',
      sell: '#8b5cf6'
    }
  },
  sunset: {
    name: 'sunset',
    colors: {
      buy: '#f59e0b',
      sell: '#ec4899'
    }
  },
  forest: {
    name: 'forest',
    colors: {
      buy: '#059669',
      sell: '#dc2626'
    }
  },
  neon: {
    name: 'neon',
    colors: {
      buy: '#06d6a0',
      sell: '#f72585'
    }
  },
  custom: {
    name: 'custom',
    colors: {
      buy: '#10b981',
      sell: '#ef4444'
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('tradingView_theme');
    return saved || 'classic';
  });

  const [customColors, setCustomColors] = useState(() => {
    const saved = localStorage.getItem('tradingView_customColors');
    return saved ? JSON.parse(saved) : {
      buy: '#10b981',
      sell: '#ef4444'
    };
  });

  // Get the current theme object
  const getCurrentTheme = () => {
    if (currentTheme === 'custom') {
      return {
        name: 'custom',
        colors: customColors
      };
    }
    return predefinedThemes[currentTheme] || predefinedThemes.classic;
  };

  // Change theme
  const changeTheme = (themeName) => {
    setCurrentTheme(themeName);
    localStorage.setItem('tradingView_theme', themeName);
  };

  // Update custom colors
  const updateCustomColors = (colors) => {
    setCustomColors(colors);
    localStorage.setItem('tradingView_customColors', JSON.stringify(colors));
  };

  const value = {
    theme: getCurrentTheme(),
    changeTheme,
    updateCustomColors
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};